import React, { useEffect } from 'react';
import {
  hasAccessWithinPolicyGroup,
  hasOgpAccessOrHqCompanyAdmin,
  PolicyGroupAndRoles,
} from 'shared/utils/policy-controller';
import { HqUserInfo } from '@types';
import {
  AuthenticatedHqGuardProps,
  AuthenticatedOgpGuardProps,
  AuthenticatedRouteV2Props,
} from 'shared/guards/authenticated-guard.types';
import { useAppSettingsProvider } from 'shared/contexts/AppSettingsContext';
import { accessAlerts as alerts, useAlert } from 'shared/components/alerts';
import { generatePath, useNavigate } from 'react-router-dom';
import { useIsHqImpersonation } from 'shared/hooks';
import { useGetOGPUserInfo } from 'ogp/hooks';
import { isLimitedOgpCompany } from 'ogp/limited-company-capabilities';
import { routePrefix as ogpPrefix } from '../../ogp/settings';
import { hqPrefix } from '../../Routes';

const AuthenticatedGuard = (props: AuthenticatedRouteV2Props) => {
  const { domain } = useAppSettingsProvider();

  if (domain === 'hq') {
    return <AuthenticatedHqGuard {...(props as AuthenticatedHqGuardProps)} />;
  }

  return <AuthenticatedOgpGuard {...(props as AuthenticatedOgpGuardProps)} />;
};

const AuthenticatedHqGuard = ({
  children,
  userInfo,
  policyGroup,
  roles,
  fallbackPath,
}: AuthenticatedRouteV2Props & { userInfo: HqUserInfo } & PolicyGroupAndRoles) => {
  const navigate = useNavigate();
  const { alertError } = useAlert();
  const redirectPath = `${hqPrefix}/${fallbackPath}`;

  React.useEffect(() => {
    if (!hasAccessWithinPolicyGroup(policyGroup, userInfo, roles)) {
      alertError(new Error(alerts.error.illegal));
      navigate(redirectPath);
    }
  }, [alertError, policyGroup, redirectPath, roles, userInfo, navigate]);

  return <>{children}</>;
};

const AuthenticatedOgpGuard = (props: AuthenticatedOgpGuardProps) => {
  const { children, userInfo, fallbackPath } = props;
  const hqImpersonationOnly = props.hqImpersonationOnly === true;
  const policyGroup = 'policyGroup' in props ? props.policyGroup : undefined;
  const roles = 'roles' in props ? props.roles : undefined;
  const userQuery = useGetOGPUserInfo();
  const navigate = useNavigate();
  const { alertError } = useAlert();
  const isHqImpersonation = useIsHqImpersonation();
  const redirectPath = `${ogpPrefix}/:companyHash/${fallbackPath ? fallbackPath : 'dashboard'}`;

  const companyType = userQuery.data?.userInfo.company.companyType;
  const isLimitedCompanyTenant = isLimitedOgpCompany(companyType);

  useEffect(() => {
    if (hqImpersonationOnly) {
      if (isHqImpersonation && !isLimitedCompanyTenant) {
        return;
      }
      alertError(new Error(alerts.error.illegal));
      navigate(generatePath(redirectPath, { companyHash: userQuery.data?.userInfo.company.slug }));
      return;
    }
    if (isHqImpersonation && !isLimitedCompanyTenant) {
      return;
    }
    if (!policyGroup || !roles || !hasOgpAccessOrHqCompanyAdmin(policyGroup, userInfo, roles)) {
      alertError(new Error(alerts.error.illegal));
      navigate(generatePath(redirectPath, { companyHash: userQuery.data?.userInfo.company.slug }));
    }
  }, [
    hqImpersonationOnly,
    isHqImpersonation,
    isLimitedCompanyTenant,
    alertError,
    policyGroup,
    redirectPath,
    roles,
    userInfo,
    navigate,
    userQuery.data?.userInfo.company.slug,
  ]);

  return <>{children}</>;
};

export { AuthenticatedGuard };
